import { Box, Flex, Text } from '@chakra-ui/react';
import assert from 'assert';
import { getIn } from 'formik';
import _ from 'lodash';
import React, { useState } from 'react';

import { typeguards as commonTypeguards } from '@finley-technologies/common';

import { WipConfig } from '../../types';
import { StickyHeaderSection } from 'packages/common-frontend/components/StickyHeaderSection';
import { AddButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/add/AddButton';
import { AddPropertyModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/add/AddPropertyModal';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { CreditAgreementSectionAccordionTable } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/property/CreditAgreementSectionAccordionTable';

export const FundingRequestComponentMechanics: React.FC<{
  fundingRequestComponentKey: keyof CreditAgreement.DomainEntity.FundingRequestMechanics;
  basePropertyKey: string;
  wipConfig?: WipConfig;
  fundingRequestComponentProperties: NonNullable<
    CreditAgreement.DomainEntity.FundingRequestMechanics[keyof CreditAgreement.DomainEntity.FundingRequestMechanics]
  >;
}> = ({
  fundingRequestComponentKey,
  wipConfig,
  basePropertyKey,
  fundingRequestComponentProperties,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  if (
    !wipConfig?.isMutateAllowed(fundingRequestComponentKey) &&
    !_.compact(Object.values(fundingRequestComponentProperties)).length
  ) {
    return null;
  }

  return (
    <Box mt="20px">
      <Flex alignItems="center" mb="2">
        <Text fontWeight="bold" fontSize="20px">
          {_.startCase(String(fundingRequestComponentKey))}
        </Text>
        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <>
            <AddButton setIsOpen={setIsOpen} />
            <AddPropertyModal
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              wipConfig={wipConfig}
              basePropertyKey={basePropertyKey}
              propertyMap={getIn(
                wipConfig.creditAgreementFullArtifactPropertyMap,
                basePropertyKey,
              )}
            />
          </>
        )}
      </Flex>
      <Box>
        <CreditAgreementSectionAccordionTable
          key={`${basePropertyKey}.${String(fundingRequestComponentKey)}`}
          wipConfig={wipConfig}
          basePropertyKey={basePropertyKey}
          // @ts-ignore
          properties={fundingRequestComponentProperties}
        />
      </Box>
    </Box>
  );
};

export const FundingRequestMechanics: React.FC<{
  fundingRequestMechanics: CreditAgreement.DomainEntity.FundingRequestMechanics;
  wipConfig?: WipConfig;
}> = ({ fundingRequestMechanics, wipConfig }) => {
  const basePropertyKey = 'fundingRequestMechanics';

  return (
    <Box mt="40px">
      <StickyHeaderSection labelText="Funding Request Mechanics">
        {_.map(
          _.toPairs({
            // default to empty object for general category since this will not be defined for legacy credit agreements
            properties: {},
            ...fundingRequestMechanics,
          }),
          ([fundingRequestComponentKey, fundingRequestComponentProperties]) => {
            assert(
              commonTypeguards.isFundingRequestGeneralPropertiesOrComponentType(
                fundingRequestComponentKey,
              ),
              `Expected ${fundingRequestComponentKey} to be a valid funding request general property or component type.`,
            );

            return (
              <FundingRequestComponentMechanics
                key={`${basePropertyKey}.${fundingRequestComponentKey}`}
                fundingRequestComponentKey={fundingRequestComponentKey}
                wipConfig={wipConfig}
                basePropertyKey={`${basePropertyKey}.${fundingRequestComponentKey}`}
                fundingRequestComponentProperties={
                  fundingRequestComponentProperties
                }
              />
            );
          },
        )}
      </StickyHeaderSection>
    </Box>
  );
};
